import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatCurrency, formatDateLocal, addFooter, BRAND_DARK, RED_500 } from './_helpers';

// Investor-facing PDF.
// Branches based on project status — Sold projects get a full waterfall +
// returns + transaction-ledger printout; everything else gets a "Capital
// Position Statement" with cost-basis snapshot.
export function generateInvestorStatement(project, waterfallData, jvAgreement) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  const isSold = project.status === 'Sold';
  const hasPendingSale = !isSold && project.sale_price && project.sale_price > 0;
  const statementType = isSold ? 'INVESTOR STATEMENT' : 'CAPITAL POSITION STATEMENT';

  const transactions = waterfallData?.transactions || [];
  const purchasePrice = waterfallData?.purchase_price || project.purchase_price || 0;
  const totalExpenses = waterfallData?.total_expenses || 0;
  const totalReimbursements = waterfallData?.total_reimbursements || 0;
  const costBasis = purchasePrice + totalExpenses - totalReimbursements;
  const jvCapital = waterfallData?.jv_capital ?? jvAgreement.capital_contributed;

  // SOP-aligned hurdle math.
  // - contractualHurdle = total investor responsibility per JV contract
  //                     = capital_contributed + their share of bucketed expenses
  // - investorCashOut   = what investor ACTUALLY paid out of pocket
  // - managerFronted    = LF-fronted-on-investor's-behalf (amount LF will be repaid first)
  // Falls back to the legacy cash-only formula if backend doesn't return them.
  const contractualHurdle = waterfallData?.contractual_hurdle
    ?? (jvCapital + (waterfallData?.investor_expenses ?? 0) - (waterfallData?.investor_reimbursements ?? 0));
  const investorCashOut = waterfallData?.investor_cash_out
    ?? (jvCapital + (waterfallData?.investor_expenses ?? 0) - (waterfallData?.investor_reimbursements ?? 0));
  const managerFronted = waterfallData?.manager_fronted_on_investor_behalf ?? 0;
  const managerReimbursementOwed = waterfallData?.manager_reimbursement_owed ?? 0;
  // Kept for legacy display compatibility (if any consumer still references it):
  const investorCostBasis = contractualHurdle;

  const daysHeld = waterfallData?.days_held || 0;
  const currentTier = waterfallData?.current_tier || 1;
  const investorSplit = waterfallData?.investor_split_percentage || 0;
  const tierInfo = waterfallData?.current_tier_info || `Tier ${currentTier} (${investorSplit}% investor split)`;

  // ── Header ────────────────────────────────────────────────
  doc.setFillColor(...BRAND_DARK);
  doc.rect(0, 0, pageWidth, 40, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text(statementType, 14, 25);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('LandFlow', 14, 33);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth - 14, 25, { align: 'right' });
  doc.setTextColor(0, 0, 0);

  // ── Project Info ─────────────────────────────────────────
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('PROJECT DETAILS', 14, 55);

  const statusLabel = (project.status || '').replace(/_/g, ' ');
  const projectInfo = [
    ['Project Name', project.name],
    ['Location', `${project.county || '-'}, ${project.state || '-'}`],
    ['Acres', project.acres ? `${Number(project.acres).toFixed(2)} acres` : '-'],
    ['Strategy', project.strategy],
    ['Status', statusLabel],
  ];
  autoTable(doc, {
    startY: 60,
    head: [],
    body: projectInfo,
    theme: 'plain',
    styles: { fontSize: 10 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 40 }, 1: { cellWidth: 60 } },
    margin: { left: 14 },
  });

  // ── Investor Info ────────────────────────────────────────
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('INVESTOR INFORMATION', 14, doc.lastAutoTable.finalY + 15);

  const investorInfo = [
    ['Investor Name', jvAgreement.investor_name],
    ['JV Capital (Purchase Responsibility)', formatCurrency(jvCapital)],
  ];
  // SOP audit fix: surface the investor's CONTRACTUAL HURDLE explicitly,
  // and split out cash-actually-paid vs. LF-fronted-on-their-behalf.
  if (managerFronted > 0) {
    investorInfo.push(['Manager-Fronted on Investor Behalf', formatCurrency(managerFronted)]);
  }
  investorInfo.push(['Total Contractual Hurdle', formatCurrency(contractualHurdle)]);
  investorInfo.push(['Of Which: Cash Out by Investor', formatCurrency(investorCashOut)]);

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 20,
    head: [],
    body: investorInfo,
    theme: 'plain',
    styles: { fontSize: 10 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 75 }, 1: { cellWidth: 55, halign: 'right' } },
    margin: { left: 14 },
  });

  // ── Per-Investor Split (pooled JV co-investor) ───────────
  // For an investor this array is pre-filtered server-side to their own slice;
  // owners printing it would see the full roster.
  const perInvestorSplit = waterfallData?.per_investor_split || [];
  if (perInvestorSplit.length > 0) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('YOUR INVESTOR SPLIT', 14, doc.lastAutoTable.finalY + 15);

    const splitRows = perInvestorSplit.map((s) => ([
      s.name || 'Investor',
      `${s.funded_pct}%`,
      formatCurrency(s.funded || s.capital_committed),
      formatCurrency(s.allocated_profit),
      formatCurrency(s.total_return),
    ]));
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 20,
      head: [['Investor', 'Share', 'Funded', 'Profit Share', 'Total Return']],
      body: splitRows,
      theme: 'striped',
      styles: { fontSize: 9 },
      headStyles: { fillColor: BRAND_DARK, fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 45 },
        1: { cellWidth: 20, halign: 'right' },
        2: { cellWidth: 35, halign: 'right' },
        3: { cellWidth: 35, halign: 'right' },
        4: { cellWidth: 35, halign: 'right' },
      },
      margin: { left: 14 },
    });
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(100, 100, 100);
    doc.text(
      `Pooled JV profit allocated by each investor's ${perInvestorSplit[0]?.basis === 'committed' ? 'committed capital' : 'funded'} share.`,
      14, doc.lastAutoTable.finalY + 6,
    );
    doc.setTextColor(0, 0, 0);
  }


  // ── Financial Summary ────────────────────────────────────
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('FINANCIAL SUMMARY', 14, doc.lastAutoTable.finalY + 15);

  const financialData = [
    ['Purchase Price', formatCurrency(purchasePrice)],
    ['Total Expenses', formatCurrency(totalExpenses)],
  ];
  if (totalReimbursements > 0) financialData.push(['Less: Reimbursements', `(${formatCurrency(totalReimbursements)})`]);
  financialData.push(['Cost Basis', formatCurrency(costBasis)]);
  if (isSold || hasPendingSale) {
    financialData.push(['Sale Price', formatCurrency(project.sale_price)]);
    const grossProfit = project.sale_price - costBasis;
    financialData.push([isSold ? 'Gross Profit' : 'Expected Gross Profit', formatCurrency(grossProfit)]);
  }

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 20,
    head: [],
    body: financialData,
    theme: 'striped',
    styles: { fontSize: 10 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 }, 1: { cellWidth: 45, halign: 'right' } },
    margin: { left: 14 },
  });

  // ── Holding Period ───────────────────────────────────────
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('HOLDING PERIOD', 14, doc.lastAutoTable.finalY + 15);

  const acquisitionDate = project.acquisition_date ? new Date(project.acquisition_date) : null;
  const holdingInfo = [
    ['Acquisition Date', acquisitionDate ? acquisitionDate.toLocaleDateString() : 'Not Set'],
    ['Days Held', `${daysHeld} days`],
    ['Current Tier', tierInfo],
  ];
  if (hasPendingSale && project.sale_date) holdingInfo.push(['Expected Close Date', formatDateLocal(project.sale_date)]);

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 20,
    head: [],
    body: holdingInfo,
    theme: 'plain',
    styles: { fontSize: 10 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 40 }, 1: { cellWidth: 60 } },
    margin: { left: 14 },
  });

  if (isSold) {
    // ── Waterfall ──────────────────────────────────────────
    const netProceeds = waterfallData?.net_proceeds || 0;
    const capitalReturned = waterfallData?.capital_returned || 0;
    const distributableProfit = waterfallData?.distributable_profit || 0;
    const investorProfit = waterfallData?.investor_profit || 0;
    const landFinderProfit = waterfallData?.land_finder_profit || 0;

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('WATERFALL CALCULATION', 14, doc.lastAutoTable.finalY + 15);

    const waterfallTable = [
      ['Net Proceeds', formatCurrency(netProceeds)],
      ['Less: Capital Return', `(${formatCurrency(capitalReturned)})`],
      ['Distributable Profit', formatCurrency(distributableProfit)],
      ['', ''],
      ['Investor Profit Share', formatCurrency(investorProfit)],
      ['Land Finder Profit Share', formatCurrency(landFinderProfit)],
    ];

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 20,
      head: [],
      body: waterfallTable,
      theme: 'striped',
      styles: { fontSize: 10 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 60 }, 1: { cellWidth: 50, halign: 'right' } },
      margin: { left: 14 },
    });

    // ── Investment Returns ────────────────────────────────
    const cashOnCash = waterfallData?.cash_on_cash_return ?? (investorCostBasis > 0 ? (investorProfit / investorCostBasis * 100) : 0);
    const annualizedRoi = waterfallData?.annualized_roi ?? (investorCostBasis > 0 && daysHeld > 0 ? ((investorProfit / investorCostBasis) / (daysHeld / 365) * 100) : 0);

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('INVESTMENT RETURNS', 14, doc.lastAutoTable.finalY + 15);

    const returnsTable = [
      ['Cash on Cash Return', `${cashOnCash >= 0 ? '+' : ''}${cashOnCash.toFixed(2)}%`],
      ['Annualized ROI', `${annualizedRoi >= 0 ? '+' : ''}${annualizedRoi.toFixed(2)}%`],
      ['Holding Period', `${daysHeld} days`],
      ['Total Investor Distribution', formatCurrency(capitalReturned + investorProfit)],
    ];

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 20,
      head: [],
      body: returnsTable,
      theme: 'plain',
      styles: { fontSize: 10 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 }, 1: { cellWidth: 45, halign: 'right' } },
      margin: { left: 14 },
    });

    // Total distribution box
    let totalY = doc.lastAutoTable.finalY + 10;
    if (totalY + 30 > 270) { doc.addPage(); totalY = 20; }
    doc.setFillColor(...BRAND_DARK);
    doc.rect(14, totalY, pageWidth - 28, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('TOTAL INVESTOR DISTRIBUTION', 20, totalY + 10);
    doc.setFontSize(16);
    doc.text(formatCurrency(capitalReturned + investorProfit), pageWidth - 20, totalY + 16, { align: 'right' });
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('(Capital Return + Profit Share)', 20, totalY + 18);
  } else {
    // ── Two-Box Capital Position (per SOP audit) ─────────────
    //   Left:  Cash Out (what investor actually paid)
    //   Right: Manager-Fronted on Investor's Behalf (LF will be repaid first)
    let boxY = doc.lastAutoTable.finalY + 10;
    if (boxY + 35 > 270) { doc.addPage(); boxY = 20; }
    const innerWidth = pageWidth - 28;
    const boxW = (innerWidth - 6) / 2;  // 6pt gap
    const boxH = 32;

    // Left box: Cash Out
    doc.setFillColor(...BRAND_DARK);
    doc.rect(14, boxY, boxW, boxH, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('CASH OUT BY INVESTOR', 14 + 6, boxY + 11);
    if (waterfallData?.investor_cash_out_estimated) {
      doc.setFillColor(245, 158, 11);  // amber-500
      doc.rect(14 + boxW - 26, boxY + 4, 22, 6, 'F');
      doc.setFontSize(6);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text('ESTIMATED', 14 + boxW - 15, boxY + 8.2, { align: 'center' });
      doc.setTextColor(255, 255, 255);
    }
    doc.setFontSize(15);
    doc.setFont('helvetica', 'bold');
    doc.text(formatCurrency(investorCashOut), 14 + boxW - 6, boxY + 19, { align: 'right' });
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(
      waterfallData?.investor_cash_out_estimated
        ? 'No wire txns logged — estimate from JV capital'
        : 'Wired + investor-paid expenses',
      14 + 6, boxY + 26,
    );

    // Right box: Manager-Fronted on Investor's Behalf
    const rightX = 14 + boxW + 6;
    doc.setFillColor(155, 100, 50);  // amber/brown to differentiate
    doc.rect(rightX, boxY, boxW, boxH, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('MANAGER-FRONTED ON MY BEHALF', rightX + 6, boxY + 11);
    doc.setFontSize(15);
    doc.text(formatCurrency(managerFronted), rightX + boxW - 6, boxY + 19, { align: 'right' });
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text('LF-paid investor-responsibility (repaid at sale)', rightX + 6, boxY + 26);

    // Combined hurdle line below
    boxY += boxH + 6;
    doc.setFillColor(245, 245, 240);
    doc.rect(14, boxY, innerWidth, 14, 'F');
    doc.setDrawColor(...BRAND_DARK);
    doc.rect(14, boxY, innerWidth, 14, 'S');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('CONTRACTUAL HURDLE (Cash + Manager-Fronted, returned before profit split)', 14 + 6, boxY + 9);
    doc.text(formatCurrency(contractualHurdle), 14 + innerWidth - 6, boxY + 9, { align: 'right' });

    if (managerReimbursementOwed > 0) {
      boxY += 18;
      doc.setTextColor(155, 100, 50);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(
        `Manager Reimbursement Owed at sale: ${formatCurrency(managerReimbursementOwed)} (LF-fronted minus investor-overpaid LF expenses)`,
        14, boxY,
      );
    }

    if (hasPendingSale) {
      boxY += 8;
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.text(`(Pending sale at ${formatCurrency(project.sale_price)})`, 14, boxY);
    }
  }

  // ── Full Transaction Ledger (page 2) ───────────────────────
  if (transactions.length > 0) {
    doc.addPage();
    doc.setFillColor(...BRAND_DARK);
    doc.rect(0, 0, pageWidth, 30, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('TRANSACTION LEDGER', 14, 20);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`${project.name} | All transactions for full transparency`, pageWidth - 14, 20, { align: 'right' });
    doc.setTextColor(0, 0, 0);

    const txRows = transactions.map(t => {
      const typeLabel = (t.type || '').replace(/_/g, ' ');
      const isCreditType = ['Reimbursement', 'Refund', 'Vendor_Reimbursement'].includes(t.type);
      const party = isCreditType
        ? (t.received_by === 'Investor' ? 'Investor' : t.received_by === 'Land_Finder' ? 'Land Finder' : '-')
        : (t.responsible_party === 'Investor' ? 'Investor' : t.responsible_party === 'Land_Finder' ? 'Land Finder' : '-');
      const isCredit = ['Reimbursement', 'Refund', 'Vendor_Reimbursement', 'Sale_Revenue'].includes(t.type);
      const amountStr = isCredit ? `(${formatCurrency(t.amount)})` : formatCurrency(t.amount);
      return [
        t.date ? formatDateLocal(t.date) : '-',
        typeLabel,
        t.category || '-',
        (t.description || '-').substring(0, 35),
        party,
        amountStr,
      ];
    });

    autoTable(doc, {
      startY: 40,
      head: [['Date', 'Type', 'Category', 'Description', 'Responsible Party', 'Amount']],
      body: txRows,
      theme: 'striped',
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: BRAND_DARK, fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 22 },
        1: { cellWidth: 28 },
        2: { cellWidth: 30 },
        3: { cellWidth: 45 },
        4: { cellWidth: 28 },
        5: { cellWidth: 28, halign: 'right' },
      },
      margin: { left: 14, right: 14 },
      didParseCell(data) {
        if (data.column.index === 5 && data.cell.raw && data.cell.raw.startsWith('(')) {
          data.cell.styles.textColor = RED_500;
        }
      },
    });

    // ── Transaction totals ─────────────────────────────
    const summaryY = doc.lastAutoTable.finalY + 10;
    const sumByTypes = (types) => transactions.filter(t => types.includes(t.type)).reduce((s, t) => s + (t.amount || 0), 0);
    const totalExpensesSum = sumByTypes(['Expense']);
    const totalEMD = sumByTypes(['EMD']);
    const totalPurchase = sumByTypes(['Purchase_Price']);
    const totalReimbSum = sumByTypes(['Reimbursement', 'Refund', 'Vendor_Reimbursement']);
    const totalOther = transactions.filter(t => !['Expense', 'EMD', 'Purchase_Price', 'Reimbursement', 'Refund', 'Vendor_Reimbursement'].includes(t.type)).reduce((s, t) => s + (t.amount || 0), 0);

    const summaryRows = [];
    if (totalPurchase > 0) summaryRows.push(['Purchase Price', formatCurrency(totalPurchase)]);
    if (totalEMD > 0) summaryRows.push(['Earnest Money Deposits', formatCurrency(totalEMD)]);
    if (totalExpensesSum > 0) summaryRows.push(['Total Expenses (Closing Costs, Due Diligence, etc.)', formatCurrency(totalExpensesSum)]);
    if (totalReimbSum > 0) summaryRows.push(['Total Reimbursements / Refunds', `(${formatCurrency(totalReimbSum)})`]);
    if (totalOther > 0) summaryRows.push(['Other Transactions', formatCurrency(totalOther)]);
    summaryRows.push(['NET TOTAL', formatCurrency(totalPurchase + totalEMD + totalExpensesSum + totalOther - totalReimbSum)]);

    if (summaryRows.length > 0) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('TRANSACTION TOTALS', 14, summaryY);

      autoTable(doc, {
        startY: summaryY + 5,
        head: [],
        body: summaryRows,
        theme: 'plain',
        styles: { fontSize: 9 },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 100 },
          1: { cellWidth: 40, halign: 'right', fontStyle: 'bold' },
        },
        margin: { left: 14 },
        didParseCell(data) {
          if (data.row.index === summaryRows.length - 1) {
            data.cell.styles.fontSize = 10;
            data.cell.styles.fillColor = [240, 253, 244];
          }
        },
      });
    }
  }

  addFooter(doc, {
    disclaimer: 'This statement is for informational purposes only. Please consult with your tax advisor.',
  });

  const filePrefix = isSold ? 'Investor_Statement' : 'Capital_Position';
  doc.save(`${filePrefix}_${project.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
}
